import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader } from "@/components/ui/loader";
import { useGetBrokerageDocumentLink } from "@/lib/api/brokerage";
import { BrokerageFile } from "@/lib/api/brokerage";

interface PDFViewerDialogProps {
  file: BrokerageFile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PDFViewerDialog({ file, open, onOpenChange }: PDFViewerDialogProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const getDocumentLink = useGetBrokerageDocumentLink();

  useEffect(() => {
    if (open && file) {
      loadDocument();
    } else {
      // Reset state when dialog closes
      setPdfUrl(null);
      setError(null);
    }
  }, [open, file?.id]);

  const loadDocument = async () => {
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await getDocumentLink(file.id);
      
      if (result.success && result.data) {
        setPdfUrl(result.data.link);
      } else {
        setError("Failed to load document. Please try again.");
      }
    } catch (err) {
      setError("An error occurred while loading the document.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[80vw] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {file?.Document?.filename || "Document Viewer"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <Loader />
            </div>
          )}
          
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {pdfUrl && !isLoading && !error && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title={file?.Document?.filename || "PDF Document"}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
